import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useStripe, PaymentRequestButtonElement } from '@stripe/react-stripe-js';
import classNames from 'classnames';
import CheckoutAnalytics from 'analytics/checkout';
import parseShippingType from '../lib/parseShippingType';

import ordersApi from '../api/orders';

const CheckoutPaymentRequest = props => {
  const stripe = useStripe();

  const [canMakePayment, setCanMakePayment] = useState(false);

  const paymentRequest = useRef();
  const walletRef = useRef();

  const accessToken = useRef(props.accessToken);
  const items = useRef(props.items);
  const amount = useRef(props.amount);
  const amountInCents = useRef(props.amountInCents);
  const currency = useRef(props.currency);
  const promoCode = useRef(props.promoCode);
  const requestShipping = useRef(props.requestShipping);

  const countryCode = useRef();
  const defaultShippingType = useRef();

  useEffect(() => {
    if (stripe) {
      paymentRequest.current = stripe.paymentRequest({
        country: 'US',
        requestShipping: true,
        requestPayerPhone: true,
        requestPayerEmail: props.requestEmail,
        shippingOptions: [],
        currency: currency.current.toLowerCase(),
        total: {
          label: 'Web Order',
          amount: amountInCents.current,
        },
      });

      paymentRequest.current.canMakePayment().then(result => {
        walletRef.current = CheckoutAnalytics.buttonWallet(result);
        setCanMakePayment(!!result);
      });
    }
  }, [stripe]);

  useEffect(() => {
    accessToken.current = props.accessToken;
    items.current = props.items;
    amount.current = props.amount;
    amountInCents.current = props.amountInCents;
    currency.current = props.currency;
    promoCode.current = props.promoCode;
    requestShipping.current = props.requestShipping;

    if (canMakePayment && !paymentRequest.current._isShowing) {
      paymentRequest.current.update({
        currency: currency.current.toLowerCase(),
        total: {
          label: 'Web Order',
          amount: amountInCents.current,
        },
      });
    }
  }, [
    canMakePayment,
    props.accessToken,
    props.items,
    props.amount,
    props.amountInCents,
    props.currency,
    props.promoCode,
    props.requestShipping,
  ]);

  useEffect(() => {
    if (canMakePayment) {
      paymentRequest.current.on('shippingaddresschange', async e => {
        const { shippingAddress } = e;

        countryCode.current = shippingAddress.country;

        const { data, error } = await ordersApi(countryCode.current).getOrderPrice(
          accessToken.current,
          items.current,
          undefined,
          promoCode.current,
        );

        if (error) {
          await e.updateWith({
            status: 'invalid_shipping_address',
          });
          return;
        }

        const {
          shipping_types: newShippingOptions,
          default_shipping_type: newShippingType,
          overall: newAmount,
          overall_in_cents: newAmountInCents,
          currency: newCurrency,
          shippable: newRequestShipping,
        } = data;

        amount.current = newAmount;
        amountInCents.current = newAmountInCents;
        currency.current = newCurrency;
        requestShipping.current = newRequestShipping;
        defaultShippingType.current = newShippingType;

        const shippingOptions = [];
        if (requestShipping.current) {
          newShippingOptions
            .map(item => ({ ...item, selected: newShippingType === item.type }))
            .sort((a, b) => b.selected - a.selected)
            .forEach(item => {
              shippingOptions.push({
                id: `${item.type}`,
                label: item.name,
                detail: `${item.description || ''}`.replace('\\n', ' '),
                amount: item.price_in_cents,
              });
            });
        } else {
          shippingOptions.push({
            id: `digital`,
            label: 'Digital',
            amount: 0,
          });
        }

        await e.updateWith({
          status: 'success',
          shippingOptions,
          total: {
            label: 'Web Order',
            amount: newAmountInCents,
          },
        });
      });

      paymentRequest.current.on('shippingoptionchange', async e => {
        const { data, error } = await ordersApi(countryCode.current).getOrderPrice(
          accessToken.current,
          items.current,
          parseShippingType(e.shippingOption.id, 10),
          promoCode.current,
        );

        if (error) {
          await e.updateWith({
            status: 'fail',
          });
          return;
        }

        const {
          overall: newAmount,
          overall_in_cents: newAmountInCents,
          currency: newCurrency,
        } = data;

        amount.current = newAmount;
        amountInCents.current = newAmountInCents;
        currency.current = newCurrency;

        await e.updateWith({
          status: 'success',
          total: {
            label: 'Web Order',
            amount: newAmountInCents,
          },
        });
      });

      paymentRequest.current.on('token', async response => {
        const paymentMethod = CheckoutAnalytics.walletMethod(response);
        const {
          token,
          complete,
          shippingAddress,
          shippingOption,
          payerPhone,
          payerEmail,
        } = response;

        const address = {
          name: shippingAddress.recipient,
          street: shippingAddress.addressLine[0] || '',
          street2: shippingAddress.addressLine[1] || '',
          city: shippingAddress.city,
          zip: shippingAddress.postalCode,
          state: shippingAddress.region,
          country: shippingAddress.country,
          country_code: shippingAddress.country,
          phone: payerPhone,
        };

        if (payerEmail) {
          address.email = payerEmail;
        }

        const shippingType = requestShipping.current
          ? parseShippingType(shippingOption.id)
          : defaultShippingType.current;

        if (token.id) {
          let succeeded;
          try {
            succeeded = await props.onSuccess({
              paymentMethod,
              countryCode: countryCode.current,
              accessToken: accessToken.current,
              paymentData: {
                stripe: {
                  source: token.id,
                },
              },
              shippingAddress: address,
              shippingType,
              items: items.current,
              promoCode: promoCode.current,
              price: amount.current,
              currency: currency.current,
            });
          } catch (error) {
            complete('fail');
            throw error;
          }
          complete(succeeded === true ? 'success' : 'fail');
        } else {
          CheckoutAnalytics.failed(paymentMethod, undefined, 'wallet_token_missing');
          complete('fail');
        }
      });
    }

    return () => {
      if (canMakePayment) {
        paymentRequest.current.off('shippingoptionchange');
        paymentRequest.current.off('shippingaddresschange');
        paymentRequest.current.off('token');
      }
    };
  }, [canMakePayment]);

  if (canMakePayment) {
    return (
      <PaymentRequestButtonElement
        onClick={() => {
          CheckoutAnalytics.started(props.amount, props.currency);
          props.onClick?.(walletRef.current);
        }}
        options={{
          paymentRequest: paymentRequest.current,
          style: {
            // https://stripe.com/docs/elements/payment-request-button#styling-the-element
            paymentRequestButton: {
              type: 'buy', // 'default' | 'donate' | 'buy', // default: 'default'
              theme: 'dark', // 'dark' | 'light' | 'light-outline', // default: 'dark'
              height: props.buttonHeight, // 40px to 64px, the width is always '100%'
            },
          },
        }}
        className={classNames({
          'd-none': props.amountInCents === 0,
          [props.className]: props.className,
        })}
      />
    );
  }

  return null;
};

CheckoutPaymentRequest.propTypes = {
  accessToken: PropTypes.string.isRequired,
  currency: PropTypes.string,
  amount: PropTypes.number,
  amountInCents: PropTypes.number,
  items: PropTypes.array,
  promoCode: PropTypes.string,
  requestEmail: PropTypes.bool,
  requestShipping: PropTypes.bool,
  // Resolves true once an order exists, including a later media upload error.
  onSuccess: PropTypes.func,
  // Called with the wallet the button opens (apple_pay, google_pay, link, payment_request).
  onClick: PropTypes.func,
  className: PropTypes.string,
  // CSS height passed to Stripe, e.g. '48px' to match a host's own checkout button.
  buttonHeight: PropTypes.string,
};

CheckoutPaymentRequest.defaultProps = {
  requestEmail: false,
  requestShipping: true,
  buttonHeight: '40px',
};

export default CheckoutPaymentRequest;
